import { useState } from 'react';
import { getResizedImage } from 'shared-helpers/common';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { getCustomContentBasedOnLoginStrategy } from 'lib/common';
import { RichTextValue } from 'components/common';
import { PageCustomContent, PagePlain } from 'components/page';
import { LoginContainer } from '.';

import type { GetPathInfoRes } from 'shared-types/responses';

interface Props {
  path: GetPathInfoRes['path'];
  next?: string | null;
}

const PathLogin = ({ path, next }: Props) => {
  const { language, messages, serverConfig, theme } = useReduxState();

  const [isStrategyLocal, setIsStrategyLocal] = useState(false);

  const handleActiveStrategyChange = (type: string) => {
    if (type === 'local' && !isStrategyLocal) setIsStrategyLocal(true);
    else if (type !== 'local' && isStrategyLocal) setIsStrategyLocal(false);
  };

  const availableAdditionalStrategies = serverConfig.authStrategies.filter(
    (strategy) => !strategy.hideFromLogin
  );

  const { customContent, customContentBeside } =
    getCustomContentBasedOnLoginStrategy({
      isStrategyLocal,
      language,
      serverConfig
    });

  const useLocalLogin =
    serverConfig.authEnableLocalLogin && !['sso'].includes(path?.userAccess);

  const usePathBackgroundImage =
    path?.picture &&
    !(
      serverConfig.pathLoginUseLoginBackgroundImage &&
      theme.loginBackgroundImage
    );

  if (!path) return null;

  return (
    <PagePlain
      backgroundImage={
        usePathBackgroundImage
          ? getResizedImage({ url: path.picture, size: 1920 })
          : undefined
      }
      backgroundPosition={
        usePathBackgroundImage ? path.picturePosition : undefined
      }
      padding={0}
      width="wide"
    >
      <div
        className={`content ${
          customContent && customContentBeside
            ? 'content-with-custom-beside'
            : 'content-without-custom-beside'
        }`}
      >
        <div className="path-content">
          <div className="path-pretitle">{messages.authSignInToPath}</div>

          <div className="path-title">{path.title}</div>

          {path.description ? (
            <div className="path-description">
              <RichTextValue html={path.description} />
            </div>
          ) : null}
        </div>

        <div className="login-content">
          {customContent ? (
            <div className="custom-content">
              <PageCustomContent customContent={customContent} />
            </div>
          ) : null}

          <div className="login-inner">
            <LoginContainer
              availableAdditionalStrategies={availableAdditionalStrategies}
              handleActiveStrategyChange={handleActiveStrategyChange}
              initialActiveStrategy={
                useLocalLogin &&
                availableAdditionalStrategies.length === 0 &&
                (!['registered', 'registered-and-guests'].includes(
                  path.userAccess
                ) ||
                  !serverConfig.loginPageRegisterLinkAsButton)
                  ? 'local'
                  : null
              }
              next={next}
              registrationLinkEnabled={
                useLocalLogin &&
                serverConfig.enableRegistration &&
                ['registered', 'registered-and-guests'].includes(
                  path.userAccess
                )
              }
              useEmbeddedRegister={true}
              useLocalLogin={useLocalLogin}
            />
          </div>
        </div>
      </div>

      <style jsx>{`
        .content {
          display: flex;
          flex-direction: column;
          flex-wrap: wrap;
        }
        .path-content {
          border-bottom: 1px solid ${theme.lightGreyDark};
          flex-grow: 1;
          padding: 20px;
        }
        .path-pretitle {
          color: ${theme.textColorLight};
          font-size: 1.6rem;
          font-weight: 600;
        }
        .path-title {
          color: ${theme.textColor};
          font-size: 2.2rem;
          font-weight: 700;
          margin-top: 10px;
        }
        .path-description {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          margin: 15px 0 5px;
        }
        .custom-content {
          padding: 20px 20px 5px;
        }
        .login-content {
          flex-shrink: 0;
          max-width: 100%;
          width: 100%;
        }
        .login-inner {
          padding: 20px;
        }
        .login-title {
          color: ${theme.textColor};
          font-size: 1.8rem;
          font-weight: 700;
        }
        @media (min-width: ${breakpoints.medium}px) {
          .content {
            flex-direction: row;
          }
          .path-content {
            padding: 25px;
          }
          .custom-content {
            padding: 25px 25px 0;
          }
          .login-inner {
            padding: 25px;
          }
          .content-with-custom-beside .path-content {
            width: 100%;
          }
          .content-without-custom-beside .path-content {
            border-bottom: none;
            border-right: 1px solid ${theme.lightGreyDark};
            width: calc(100% - 320px);
          }
          .content-with-custom-beside .login-content {
            display: flex;
          }
          .content-without-custom-beside .login-content {
            width: 320px;
          }
          .content-with-custom-beside .login-inner {
            width: 320px;
          }
          .content-with-custom-beside .custom-content {
            border-right: 1px solid ${theme.lightGreyDark};
            width: calc(100% - 320px);
            padding: 25px;
          }
        }
      `}</style>
    </PagePlain>
  );
};

export default PathLogin;
